import { LoadingOutlined } from '@ant-design/icons'
import type { SupplyOptionDirectiveInfo } from '@components/OrderWizard/Panes/DisplayPane/DisplayComponents/SupplyOptions/OrderDirectivePopover/types'
import { addCommasToNumber, Texto } from '@gravitate-js/excalibrr'
import { Tooltip } from 'antd'
import React from 'react'

type IntradayProgressBarProps = {
  directive: SupplyOptionDirectiveInfo | Gravitate.Directive
  barHeight?: number
  addPadding?: boolean
  isLoading?: boolean
  showTargets?: boolean
  showPlanned?: boolean
}

export function IntradayProgressBar({
  directive,
  barHeight = 30,
  addPadding = true,
  isLoading = false,
  showTargets = true,
  showPlanned,
}: IntradayProgressBarProps) {
  const barTotal = addPadding ? directive.directive_max * 1.1 : directive.directive_max
  const loadedWidth = (directive.loaded / barTotal) * 100
  const scheduledWidth = (directive.scheduled / barTotal) * 100
  const carrierWidth = (directive.carrier / barTotal) * 100
  const plannedWidth = (directive.planned / barTotal) * 100
  const minPosition = (directive.directive_min / barTotal) * 100
  const maxPosition = (directive.directive_max / barTotal) * 100
  const showHigh = showTargets && !isLoading && directive.directive_max
  const showLow = showTargets && !isLoading && directive.directive_min

  const formattedLoadedValue = addCommasToNumber(directive.loaded)
  const formattedCarrierValue = addCommasToNumber(directive.carrier)
  const formattedScheduledValue = addCommasToNumber(directive.scheduled)
  const formattedPlannedValue = addCommasToNumber(directive.planned)

  return (
    <div
      className='flex-1 flex super-progress'
      style={{
        height: barHeight,
        position: 'relative',
        width: '100%',
      }}
    >
      <div
        className='flex items-center justify-center'
        style={{
          background: 'var(--theme-color-1)',
          width: isLoading ? 60 : 0,
          height: barHeight,
          transition: '0.25s ease',
          transitionProperty: 'width',
          overflow: 'hidden',
        }}
      >
        <Texto appearance='white' category='h5'>
          <LoadingOutlined />
        </Texto>
      </div>

      {!!showLow && (
        <div
          className='target pl-1'
          style={{
            height: barHeight,
            left: `${minPosition}%`,
            position: 'absolute',
          }}
        />
      )}
      {!!showHigh && (
        <div
          className='target pl-1'
          style={{
            height: barHeight,
            left: `${maxPosition}%`,
            position: 'absolute',
          }}
        />
      )}
      <Tooltip title={`Loaded: ${formattedLoadedValue}`}>
        <div className='bar1' style={{ width: `${loadedWidth}%`, height: barHeight }} />
      </Tooltip>

      <Tooltip title={`Carrier Volume: ${formattedCarrierValue}`}>
        <div className='bar2' style={{ width: `${carrierWidth}%`, height: barHeight }} />
      </Tooltip>
      <Tooltip title={`Scheduled: ${formattedScheduledValue}`}>
        <div
          className='bar3'
          style={{
            width: `${scheduledWidth}%`,
            height: barHeight,
          }}
        />
      </Tooltip>
      {showPlanned && (
        <Tooltip title={`Planned: ${formattedPlannedValue}`}>
          <div
            className='bar4'
            style={{
              width: `${plannedWidth}%`,
              height: barHeight,
            }}
          />
        </Tooltip>
      )}
    </div>
  )
}
